/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Code } from '@atlaskit/code';
import Heading, { HeadingContextProvider } from '@atlaskit/heading';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import SectionLink from '../section-link';

type Workspace = {
  repository?: string;
  name: string;
  group?: string;
  directoryName: string;
};

const headerStyles = css({
  boxSizing: 'border-box',
  minWidth: '8ch',
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  color: token('color.text.subtlest'),
  fontWeight: 'normal',
});

const tBodyStyles = css({
  display: 'grid',
  columnGap: token('space.100'),
  borderBlockEnd: 'none',
  [media.above.sm]: {
    gridTemplateColumns: '2fr 1fr',
  },
});

/**
 * __Code docs header__
 *
 * A code docs header for the code page on atlassian design.
 */
const CodeDocsHeader = ({
  repository,
  name,
  group = 'design-system',
  directoryName,
}: Workspace) => {
  return (
    <HeadingContextProvider>
      <SectionLink id="installation" label="Installation">
        <Heading level="h700">Installation</Heading>
      </SectionLink>
      <table>
        <caption style={{ margin: 0 }}>
          <VisuallyHidden>Package installation information</VisuallyHidden>
        </caption>
        <tbody css={tBodyStyles}>
          <tr>
            <th scope="row" css={headerStyles}>
              Install
            </th>
            <td>
              <Code>yarn add {name}</Code>
            </td>
          </tr>
          {repository && (
            <tr>
              <th scope="row" css={headerStyles}>
                Source
              </th>
              <td>
                <a
                  href={`${repository}/src/master/${group}/${directoryName}`}
                  target="_blank"
                >
                  Bitbucket.org
                </a>
              </td>
            </tr>
          )}
          <tr>
            <th scope="row" css={headerStyles}>
              npm
            </th>
            <td>
              <a href={`https://www.npmjs.com/package/${name}`} target="_blank">
                {name}
              </a>
            </td>
          </tr>
          <tr>
            <th scope="row" css={headerStyles}>
              Bundle
            </th>
            <td>
              <a href={`https://unpkg.com/${name}/dist/`} target="_blank">
                unpkg.com
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </HeadingContextProvider>
  );
};

export default CodeDocsHeader;
